import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
  Monitor, Cpu, HardDrive, Wifi, Shield, 
  TrendingUp, Loader2, AlertCircle, CheckCircle,
  Ticket, Download, Zap, Settings, ArrowRight, Send, Sparkles
} from "lucide-react";
import MainLayout from "../components/MainLayout";
import SEO, { pageSEO } from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DiagnosticPage = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [currentTest, setCurrentTest] = useState("");
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [analyzingAI, setAnalyzingAI] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [senderInfo, setSenderInfo] = useState({
    name: "",
    email: "",
    phone: "",
    notes: ""
  });

  const NOT_SUPPORTED = "Not supported by this browser";

  // ========== REAL DATA COLLECTION FUNCTIONS ==========

  // System Information - Real browser data only
  const collectSystemInfo = () => {
    const ua = navigator.userAgent;
    const info = {
      platform: navigator.platform || NOT_SUPPORTED,
      userAgent: ua,
      language: navigator.language || NOT_SUPPORTED,
      languages: navigator.languages?.join(", ") || NOT_SUPPORTED,
      cookiesEnabled: navigator.cookieEnabled,
      online: navigator.onLine,
      doNotTrack: navigator.doNotTrack === "1",
    };

    // OS Detection from userAgent (interpretation, not simulation)
    let os = "Unknown";
    if (ua.includes("Windows NT 10")) os = "Windows 10/11";
    else if (ua.includes("Windows")) os = "Windows";
    else if (ua.includes("Mac OS X")) os = "macOS";
    else if (ua.includes("CrOS")) os = "Chrome OS";
    else if (ua.includes("Linux")) os = "Linux";
    else if (ua.includes("Android")) os = "Android";
    else if (/iPhone|iPad|iPod/.test(ua)) os = "iOS";
    info.os = os;

    // Browser Detection from userAgent
    let browser = "Unknown";
    let browserVersion = "";
    if (ua.includes("Chrome") && !ua.includes("Edg") && !ua.includes("OPR")) {
      browser = "Chrome";
      browserVersion = ua.match(/Chrome\/(\d+\.\d+)/)?.[1] || "";
    } else if (ua.includes("Firefox")) {
      browser = "Firefox";
      browserVersion = ua.match(/Firefox\/(\d+\.\d+)/)?.[1] || "";
    } else if (ua.includes("Safari") && !ua.includes("Chrome")) {
      browser = "Safari";
      browserVersion = ua.match(/Version\/(\d+\.\d+)/)?.[1] || "";
    } else if (ua.includes("Edg")) {
      browser = "Edge";
      browserVersion = ua.match(/Edg\/(\d+\.\d+)/)?.[1] || "";
    }
    info.browser = browser;
    info.browserVersion = browserVersion;

    // Screen info - real data
    info.screenWidth = window.screen.width;
    info.screenHeight = window.screen.height;
    info.screenColorDepth = window.screen.colorDepth;
    info.devicePixelRatio = window.devicePixelRatio;
    info.touchSupport = navigator.maxTouchPoints > 0;

    // Timezone
    try {
      info.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      info.timezone = NOT_SUPPORTED;
    }

    return info;
  };

  // Hardware Info - Only what browser actually provides
  const collectHardwareInfo = () => {
    const info = {
      cpuCores: navigator.hardwareConcurrency || NOT_SUPPORTED,
      deviceMemory: navigator.deviceMemory ? `${navigator.deviceMemory} GB` : NOT_SUPPORTED,
    };
    return info;
  };

  // Network Info - Real connection API data
  const collectNetworkInfo = () => {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    
    if (!conn) {
      return {
        supported: false,
        effectiveType: NOT_SUPPORTED,
        downlink: NOT_SUPPORTED,
        rtt: NOT_SUPPORTED,
        saveData: NOT_SUPPORTED,
      };
    }

    return {
      supported: true,
      effectiveType: conn.effectiveType?.toUpperCase() || NOT_SUPPORTED,
      downlink: conn.downlink !== undefined ? `${conn.downlink} Mbps` : NOT_SUPPORTED,
      rtt: conn.rtt !== undefined ? `${conn.rtt} ms` : NOT_SUPPORTED,
      saveData: conn.saveData !== undefined ? (conn.saveData ? "Yes" : "No") : NOT_SUPPORTED,
    };
  };

  // Battery Info - Real Battery API
  const collectBatteryInfo = async () => {
    if (!navigator.getBattery) {
      return {
        supported: false,
        level: NOT_SUPPORTED,
        charging: NOT_SUPPORTED,
        chargingTime: NOT_SUPPORTED,
        dischargingTime: NOT_SUPPORTED,
      };
    }

    try {
      const battery = await navigator.getBattery();
      return {
        supported: true,
        level: `${Math.round(battery.level * 100)}%`,
        charging: battery.charging ? "Yes" : "No",
        chargingTime: battery.chargingTime === Infinity ? "N/A" : 
          battery.chargingTime ? `${Math.round(battery.chargingTime / 60)} minutes` : "N/A",
        dischargingTime: battery.dischargingTime === Infinity ? "N/A" : 
          battery.dischargingTime ? `${Math.round(battery.dischargingTime / 60)} minutes` : "N/A",
      };
    } catch {
      return {
        supported: false,
        level: NOT_SUPPORTED,
        charging: NOT_SUPPORTED,
        chargingTime: NOT_SUPPORTED,
        dischargingTime: NOT_SUPPORTED,
      };
    }
  };

  // WebGL Info - Real GPU capability detection
  const collectWebGLInfo = () => {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl2') || canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      
      if (!gl) {
        return {
          supported: false,
          version: NOT_SUPPORTED,
          vendor: NOT_SUPPORTED,
          renderer: NOT_SUPPORTED,
          maxTextureSize: NOT_SUPPORTED,
        };
      }

      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      
      return {
        supported: true,
        version: gl.getParameter(gl.VERSION) || "WebGL",
        vendor: debugInfo ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) : gl.getParameter(gl.VENDOR),
        renderer: debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER),
        maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE),
        maxViewportDims: gl.getParameter(gl.MAX_VIEWPORT_DIMS)?.join(" x "),
      };
    } catch {
      return {
        supported: false,
        version: NOT_SUPPORTED,
        vendor: NOT_SUPPORTED,
        renderer: NOT_SUPPORTED,
        maxTextureSize: NOT_SUPPORTED,
      };
    }
  };

  // Memory Info - Real JS heap data (Chrome only)
  const collectMemoryInfo = () => {
    if (!performance.memory) {
      return {
        supported: false,
        jsHeapSizeLimit: NOT_SUPPORTED,
        totalJSHeapSize: NOT_SUPPORTED,
        usedJSHeapSize: NOT_SUPPORTED,
      };
    }

    return {
      supported: true,
      jsHeapSizeLimit: `${Math.round(performance.memory.jsHeapSizeLimit / 1048576)} MB`,
      totalJSHeapSize: `${Math.round(performance.memory.totalJSHeapSize / 1048576)} MB`,
      usedJSHeapSize: `${Math.round(performance.memory.usedJSHeapSize / 1048576)} MB`,
    };
  };

  // CPU Benchmark - Real timed operations
  const runCPUBenchmark = async () => {
    const iterations = 500000;
    const results = [];

    // Run 3 times to get average
    for (let run = 0; run < 3; run++) {
      const start = performance.now();
      let value = 0;
      for (let i = 0; i < iterations; i++) {
        value += Math.sqrt(i) * Math.sin(i) * Math.cos(i);
      }
      const duration = performance.now() - start;
      results.push(Math.round(iterations / duration * 1000)); // ops per second
    }

    const avgScore = Math.round(results.reduce((a, b) => a + b) / results.length);
    const variance = results.reduce((sum, r) => sum + Math.pow(r - avgScore, 2), 0) / results.length;
    const consistency = Math.sqrt(variance) / avgScore * 100;

    // Rating based on actual benchmark score
    let rating = "Low";
    if (avgScore > 50000000) rating = "Excellent";
    else if (avgScore > 30000000) rating = "Good";
    else if (avgScore > 15000000) rating = "Moderate";

    return {
      score: avgScore.toLocaleString(),
      rating,
      consistency: consistency < 5 ? "Stable" : consistency < 15 ? "Moderate" : "Variable",
      rawScores: results,
    };
  };

  // ========== ADVANCED PERFORMANCE DEGRADATION TESTS ==========

  // CPU Throttling Test - Run benchmark multiple times and detect performance drops
  const runCPUThrottlingTest = async (updateProgress) => {
    const iterations = 300000; // Smaller iterations for faster repeated tests
    const testRuns = 5;
    const scores = [];
    
    for (let run = 0; run < testRuns; run++) {
      if (updateProgress) updateProgress(`CPU Throttling Test: Run ${run + 1}/${testRuns}...`);
      
      const start = performance.now();
      let value = 0;
      for (let i = 0; i < iterations; i++) {
        value += Math.sqrt(i) * Math.sin(i) * Math.cos(i);
      }
      const duration = performance.now() - start;
      scores.push(Math.round(iterations / duration * 1000));
      
      // Small delay between runs to allow potential throttling to occur
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    const firstScore = scores[0];
    const lastScore = scores[scores.length - 1];
    const avgScore = Math.round(scores.reduce((a, b) => a + b) / scores.length);
    const minScore = Math.min(...scores);
    const maxScore = Math.max(...scores);
    
    // Calculate performance drop percentage
    const dropPercent = ((firstScore - lastScore) / firstScore) * 100;
    const variancePercent = ((maxScore - minScore) / avgScore) * 100;
    
    let throttlingDetected = "None";
    let severity = "good";
    let message = "CPU performance remained stable across all test runs.";
    
    if (dropPercent > 20) {
      throttlingDetected = "Significant";
      severity = "bad";
      message = "Possible CPU thermal throttling detected. Performance dropped significantly during sustained load.";
    } else if (dropPercent > 10 || variancePercent > 20) {
      throttlingDetected = "Minor";
      severity = "moderate";
      message = "Minor CPU performance variation detected. This may indicate early thermal throttling or background activity.";
    }
    
    return {
      supported: true,
      scores: scores.map(s => s.toLocaleString()),
      firstScore: firstScore.toLocaleString(),
      lastScore: lastScore.toLocaleString(),
      avgScore: avgScore.toLocaleString(),
      dropPercent: Math.round(dropPercent * 10) / 10,
      variancePercent: Math.round(variancePercent * 10) / 10,
      throttlingDetected,
      severity,
      message,
    };
  };

  // GPU FPS Stability Test - WebGL animation measuring FPS over time
  const runGPUStabilityTest = async (updateProgress) => {
    // Check WebGL support first
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 256;
    const gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
    
    if (!gl) {
      return {
        supported: false,
        message: NOT_SUPPORTED,
      };
    }
    
    if (updateProgress) updateProgress("GPU Stability Test: Running WebGL animation...");
    
    const fpsReadings = [];
    const testDuration = 8000; // 8 seconds
    const startTime = performance.now();
    let frameCount = 0;
    let lastSecond = startTime;
    let currentFPS = 0;
    
    // Create a simple but intensive WebGL workload
    const vertices = new Float32Array(3000); // 1000 triangles
    for (let i = 0; i < vertices.length; i++) {
      vertices[i] = Math.random() * 2 - 1;
    }
    
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);
    
    // Simple vertex shader
    const vsSource = `
      attribute vec4 aPosition;
      uniform float uTime;
      void main() {
        gl_Position = aPosition + vec4(sin(uTime) * 0.1, cos(uTime) * 0.1, 0.0, 0.0);
        gl_PointSize = 2.0;
      }
    `;
    
    // Simple fragment shader
    const fsSource = `
      precision mediump float;
      void main() {
        gl_FragColor = vec4(0.5, 0.7, 1.0, 1.0);
      }
    `;
    
    const compileShader = (type, source) => {
      const shader = gl.createShader(type);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      return shader;
    };
    
    const vs = compileShader(gl.VERTEX_SHADER, vsSource);
    const fs = compileShader(gl.FRAGMENT_SHADER, fsSource);
    const program = gl.createProgram();
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    gl.useProgram(program);
    
    const positionLoc = gl.getAttribLocation(program, 'aPosition');
    const timeLoc = gl.getUniformLocation(program, 'uTime');
    gl.enableVertexAttribArray(positionLoc);
    gl.vertexAttribPointer(positionLoc, 3, gl.FLOAT, false, 0, 0);
    
    return new Promise((resolve) => {
      const renderFrame = () => {
        const now = performance.now();
        const elapsed = now - startTime;
        
        if (elapsed >= testDuration) {
          // Record final FPS reading
          if (frameCount > 0) {
            fpsReadings.push(currentFPS);
          }
          
          // Cleanup
          gl.deleteBuffer(buffer);
          gl.deleteShader(vs);
          gl.deleteShader(fs);
          gl.deleteProgram(program);
          
          // Analyze results
          if (fpsReadings.length < 2) {
            resolve({
              supported: true,
              fpsReadings: fpsReadings,
              avgFPS: fpsReadings[0] || 0,
              degradationDetected: "Unknown",
              severity: "neutral",
              message: "Test completed but insufficient data for analysis.",
            });
            return;
          }
          
          const firstHalf = fpsReadings.slice(0, Math.floor(fpsReadings.length / 2));
          const secondHalf = fpsReadings.slice(Math.floor(fpsReadings.length / 2));
          const avgFirst = firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length;
          const avgSecond = secondHalf.reduce((a, b) => a + b, 0) / secondHalf.length;
          const avgFPS = Math.round(fpsReadings.reduce((a, b) => a + b, 0) / fpsReadings.length);
          const minFPS = Math.min(...fpsReadings);
          const maxFPS = Math.max(...fpsReadings);
          
          const dropPercent = ((avgFirst - avgSecond) / avgFirst) * 100;
          const variancePercent = ((maxFPS - minFPS) / avgFPS) * 100;
          
          let degradationDetected = "None";
          let severity = "good";
          let message = "GPU performance remained stable throughout the test.";
          
          if (dropPercent > 20) {
            degradationDetected = "Significant";
            severity = "bad";
            message = "Possible GPU performance degradation (thermal throttling). FPS dropped significantly during sustained rendering.";
          } else if (dropPercent > 10 || variancePercent > 30) {
            degradationDetected = "Minor";
            severity = "moderate";
            message = "Minor GPU performance variation detected. This may indicate early thermal throttling or driver issues.";
          }
          
          resolve({
            supported: true,
            fpsReadings: fpsReadings,
            avgFPS: avgFPS,
            minFPS: minFPS,
            maxFPS: maxFPS,
            firstHalfAvg: Math.round(avgFirst),
            secondHalfAvg: Math.round(avgSecond),
            dropPercent: Math.round(dropPercent * 10) / 10,
            degradationDetected,
            severity,
            message,
          });
          return;
        }
        
        // Render
        gl.clearColor(0.1, 0.1, 0.15, 1.0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.uniform1f(timeLoc, elapsed / 1000);
        gl.drawArrays(gl.TRIANGLES, 0, vertices.length / 3);
        
        frameCount++;
        
        // Calculate FPS every second
        if (now - lastSecond >= 1000) {
          currentFPS = Math.round(frameCount * 1000 / (now - lastSecond));
          fpsReadings.push(currentFPS);
          frameCount = 0;
          lastSecond = now;
          
          if (updateProgress) {
            updateProgress(`GPU Stability Test: ${Math.round(elapsed / 1000)}s - ${currentFPS} FPS`);
          }
        }
        
        requestAnimationFrame(renderFrame);
      };
      
      requestAnimationFrame(renderFrame);
    });
  };

  // Stress Test - Combined CPU + GPU load
  const runStressTest = async (updateProgress) => {
    if (updateProgress) updateProgress("Stress Test: Initializing combined CPU + GPU load...");
    
    const testDuration = 10000; // 10 seconds
    const startTime = performance.now();
    const cpuScores = [];
    const gpuFpsReadings = [];
    
    // Check WebGL support
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 256;
    const gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
    
    const gpuSupported = !!gl;
    let gpuProgram, gpuBuffer, gpuTimeLoc, gpuVertices;
    
    if (gpuSupported) {
      // Setup WebGL
      gpuVertices = new Float32Array(1500);
      for (let i = 0; i < gpuVertices.length; i++) {
        gpuVertices[i] = Math.random() * 2 - 1;
      }
      
      gpuBuffer = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, gpuBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, gpuVertices, gl.STATIC_DRAW);
      
      const vsSource = `
        attribute vec4 aPosition;
        uniform float uTime;
        void main() {
          gl_Position = aPosition + vec4(sin(uTime) * 0.1, cos(uTime) * 0.1, 0.0, 0.0);
          gl_PointSize = 2.0;
        }
      `;
      const fsSource = `
        precision mediump float;
        void main() {
          gl_FragColor = vec4(1.0, 0.5, 0.3, 1.0);
        }
      `;
      
      const compileShader = (type, source) => {
        const shader = gl.createShader(type);
        gl.shaderSource(shader, source);
        gl.compileShader(shader);
        return shader;
      };
      
      const vs = compileShader(gl.VERTEX_SHADER, vsSource);
      const fs = compileShader(gl.FRAGMENT_SHADER, fsSource);
      gpuProgram = gl.createProgram();
      gl.attachShader(gpuProgram, vs);
      gl.attachShader(gpuProgram, fs);
      gl.linkProgram(gpuProgram);
      gl.useProgram(gpuProgram);
      
      const posLoc = gl.getAttribLocation(gpuProgram, 'aPosition');
      gpuTimeLoc = gl.getUniformLocation(gpuProgram, 'uTime');
      gl.enableVertexAttribArray(posLoc);
      gl.vertexAttribPointer(posLoc, 3, gl.FLOAT, false, 0, 0);
    }
    
    let gpuFrameCount = 0;
    let lastFpsCheck = startTime;
    
    return new Promise((resolve) => {
      const runIteration = async () => {
        const now = performance.now();
        const elapsed = now - startTime;
        
        if (elapsed >= testDuration) {
          // Cleanup GPU resources
          if (gpuSupported) {
            gl.deleteBuffer(gpuBuffer);
            gl.deleteProgram(gpuProgram);
          }
          
          // Analyze results
          const cpuFirst = cpuScores.slice(0, Math.floor(cpuScores.length / 2));
          const cpuSecond = cpuScores.slice(Math.floor(cpuScores.length / 2));
          const cpuAvgFirst = cpuFirst.length ? cpuFirst.reduce((a, b) => a + b) / cpuFirst.length : 0;
          const cpuAvgSecond = cpuSecond.length ? cpuSecond.reduce((a, b) => a + b) / cpuSecond.length : 0;
          const cpuDropPercent = cpuAvgFirst > 0 ? ((cpuAvgFirst - cpuAvgSecond) / cpuAvgFirst) * 100 : 0;
          
          let gpuDropPercent = 0;
          if (gpuSupported && gpuFpsReadings.length >= 2) {
            const gpuFirst = gpuFpsReadings.slice(0, Math.floor(gpuFpsReadings.length / 2));
            const gpuSecond = gpuFpsReadings.slice(Math.floor(gpuFpsReadings.length / 2));
            const gpuAvgFirst = gpuFirst.reduce((a, b) => a + b) / gpuFirst.length;
            const gpuAvgSecond = gpuSecond.reduce((a, b) => a + b) / gpuSecond.length;
            gpuDropPercent = ((gpuAvgFirst - gpuAvgSecond) / gpuAvgFirst) * 100;
          }
          
          let overallResult = "Passed";
          let severity = "good";
          let message = "System handled combined CPU + GPU stress without significant performance degradation.";
          
          if (cpuDropPercent > 20 || gpuDropPercent > 20) {
            overallResult = "Failed";
            severity = "bad";
            message = "Significant performance degradation detected under stress. This may indicate thermal throttling, insufficient cooling, or power limitations.";
          } else if (cpuDropPercent > 10 || gpuDropPercent > 10) {
            overallResult = "Warning";
            severity = "moderate";
            message = "Moderate performance variation detected under stress. Monitor system temperatures during heavy workloads.";
          }
          
          resolve({
            supported: true,
            duration: `${Math.round(elapsed / 1000)} seconds`,
            cpuScores: cpuScores.length,
            gpuSupported: gpuSupported,
            gpuFpsReadings: gpuFpsReadings.length,
            cpuDropPercent: Math.round(cpuDropPercent * 10) / 10,
            gpuDropPercent: gpuSupported ? Math.round(gpuDropPercent * 10) / 10 : "N/A",
            overallResult,
            severity,
            message,
          });
          return;
        }
        
        // CPU work - mini benchmark
        const cpuStart = performance.now();
        let value = 0;
        const cpuIterations = 100000;
        for (let i = 0; i < cpuIterations; i++) {
          value += Math.sqrt(i) * Math.sin(i);
        }
        const cpuDuration = performance.now() - cpuStart;
        cpuScores.push(Math.round(cpuIterations / cpuDuration * 1000));
        
        // GPU work
        if (gpuSupported) {
          gl.clearColor(0.15, 0.1, 0.1, 1.0);
          gl.clear(gl.COLOR_BUFFER_BIT);
          gl.uniform1f(gpuTimeLoc, elapsed / 1000);
          gl.drawArrays(gl.TRIANGLES, 0, gpuVertices.length / 3);
          gpuFrameCount++;
          
          // Record FPS every second
          if (now - lastFpsCheck >= 1000) {
            gpuFpsReadings.push(Math.round(gpuFrameCount * 1000 / (now - lastFpsCheck)));
            gpuFrameCount = 0;
            lastFpsCheck = now;
          }
        }
        
        if (updateProgress) {
          updateProgress(`Stress Test: ${Math.round(elapsed / 1000)}s / 10s`);
        }
        
        // Continue in next frame
        requestAnimationFrame(runIteration);
      };
      
      requestAnimationFrame(runIteration);
    });
  };

  // Internet Speed Test - REAL measurements only
  const runSpeedTest = async (onProgress) => {
    const API_BASE = process.env.REACT_APP_BACKEND_URL;
    const results = {
      download: null,
      upload: null,
      latency: null,
      testStatus: "Testing...",
      downloadProgress: 0,
      uploadProgress: 0,
      error: null,
    };

    try {
      // === STEP 1: Latency Test (Real GET requests to our server) ===
      if (onProgress) onProgress({ phase: "latency", progress: 0 });
      
      const pingResults = [];
      const pingCount = 5;
      
      for (let i = 0; i < pingCount; i++) {
        try {
          const start = performance.now();
          await fetch(`${API_BASE}/api/speedtest/ping?t=${Date.now()}`, { 
            method: 'GET',
            cache: 'no-store' 
          });
          const elapsed = performance.now() - start;
          pingResults.push(Math.round(elapsed));
          if (onProgress) onProgress({ phase: "latency", progress: ((i + 1) / pingCount) * 100 });
        } catch (e) {
          // Skip failed pings
        }
      }
      
      if (pingResults.length > 0) {
        // Remove outliers (highest and lowest) if we have enough samples
        if (pingResults.length >= 4) {
          pingResults.sort((a, b) => a - b);
          pingResults.shift(); // Remove lowest
          pingResults.pop();   // Remove highest
        }
        results.latency = Math.round(pingResults.reduce((a, b) => a + b, 0) / pingResults.length);
      } else {
        results.latency = null;
      }

      // === STEP 2: Download Speed Test (Real file download from our backend) ===
      if (onProgress) onProgress({ phase: "download", progress: 0 });
      
      // Download 10MB test file from our own server (CORS enabled)
      const downloadSizeMB = 10;
      const downloadUrl = `${API_BASE}/api/speedtest/download?size=${downloadSizeMB}&t=${Date.now()}`;
      
      try {
        const downloadStart = performance.now();
        const response = await fetch(downloadUrl, { 
          cache: 'no-store'
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        // Get the response as a stream to track progress
        const reader = response.body.getReader();
        const contentLength = parseInt(response.headers.get('Content-Length') || String(downloadSizeMB * 1024 * 1024));
        
        let receivedBytes = 0;
        
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          
          receivedBytes += value.length;
          
          if (contentLength > 0 && onProgress) {
            const progress = Math.min((receivedBytes / contentLength) * 100, 100);
            const mbDownloaded = (receivedBytes / (1024 * 1024)).toFixed(1);
            onProgress({ phase: "download", progress, bytes: receivedBytes, total: contentLength, mb: mbDownloaded });
          }
        }
        
        const downloadEnd = performance.now();
        const downloadTimeSeconds = (downloadEnd - downloadStart) / 1000;
        
        if (receivedBytes > 0 && downloadTimeSeconds > 0) {
          // Calculate: (bytes * 8 bits) / seconds / 1,000,000 = Mbps
          const downloadSpeedMbps = (receivedBytes * 8 / downloadTimeSeconds) / 1000000;
          results.download = Math.round(downloadSpeedMbps * 10) / 10;
        } else {
          results.download = null;
          results.error = "Download test failed: no data received";
        }
      } catch (e) {
        results.download = null;
        results.error = "Download test failed: unable to access test file";
        console.error("Download speed test error:", e);
      }

      // === STEP 3: Upload Speed Test (Real data upload) ===
      if (onProgress) onProgress({ phase: "upload", progress: 0 });
      
      // Create 1MB of random data for upload using smaller chunks
      const uploadSizeMB = 1;
      const uploadSizeBytes = uploadSizeMB * 1024 * 1024;
      const uploadData = new Uint8Array(uploadSizeBytes);
      
      // Fill in chunks (crypto.getRandomValues has a 65536 byte limit)
      const chunkSize = 65536;
      for (let i = 0; i < uploadSizeBytes; i += chunkSize) {
        const chunk = new Uint8Array(Math.min(chunkSize, uploadSizeBytes - i));
        crypto.getRandomValues(chunk);
        uploadData.set(chunk, i);
      }
      
      try {
        const uploadStart = performance.now();
        
        const uploadResponse = await fetch(`${API_BASE}/api/speedtest/upload?t=${Date.now()}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/octet-stream',
          },
          body: uploadData,
        });
        
        const uploadEnd = performance.now();
        
        if (!uploadResponse.ok) {
          throw new Error(`Upload failed: ${uploadResponse.status}`);
        }
        
        const uploadResult = await uploadResponse.json();
        const uploadTimeSeconds = (uploadEnd - uploadStart) / 1000;
        
        if (uploadResult.bytes_received > 0 && uploadTimeSeconds > 0) {
          // Calculate: (bytes * 8 bits) / seconds / 1,000,000 = Mbps
          const uploadSpeedMbps = (uploadResult.bytes_received * 8 / uploadTimeSeconds) / 1000000;
          results.upload = Math.round(uploadSpeedMbps * 10) / 10;
        } else {
          results.upload = null;
        }
        
        if (onProgress) onProgress({ phase: "upload", progress: 100 });
      } catch (e) {
        results.upload = null;
        if (!results.error) {
          results.error = `Upload test failed: ${e.message}`;
        }
      }

      // === Determine final status ===
      if (results.download !== null || results.upload !== null) {
        results.testStatus = "Completed";
      } else if (results.error) {
        results.testStatus = `Failed - ${results.error}`;
      } else {
        results.testStatus = "Failed - Could not complete speed test";
      }
      
    } catch (e) {
      results.testStatus = `Failed - ${e.message}`;
      results.error = e.message;
    }

    return results;
  };

  // Storage Info - Official Storage Estimate API ONLY
  // Note: This shows BROWSER storage (IndexedDB, Cache, etc.), NOT system disk storage
  const collectStorageInfo = async () => {
    if (!navigator.storage || !navigator.storage.estimate) {
      return {
        supported: false,
        quota: null,
        usage: null,
        quotaMB: null,
        usageMB: null,
        message: "Storage information is not supported by this browser.",
      };
    }

    try {
      const estimate = await navigator.storage.estimate();
      
      // Convert to MB for display
      const quotaMB = estimate.quota ? Math.round(estimate.quota / (1024 * 1024)) : null;
      const usageMB = estimate.usage ? Math.round(estimate.usage / (1024 * 1024) * 100) / 100 : null;
      
      return {
        supported: true,
        quota: quotaMB,
        usage: usageMB,
        quotaMB: quotaMB ? `${quotaMB} MB` : "Not available",
        usageMB: usageMB !== null ? `${usageMB} MB` : "Not available",
        message: null,
      };
    } catch (e) {
      return {
        supported: false,
        quota: null,
        usage: null,
        quotaMB: null,
        usageMB: null,
        message: "Storage information is not supported by this browser.",
      };
    }
  };

  // Security Checks - Real browser capabilities
  const collectSecurityInfo = () => {
    return {
      https: window.location.protocol === 'https:',
      secureContext: window.isSecureContext,
      cookiesEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack === "1" || navigator.doNotTrack === "yes",
    };
  };

  // ========== RUN ALL DIAGNOSTICS ==========
  const runDiagnostics = async () => {
    setIsRunning(true);
    setResults(null);
    setAiAnalysis(null);
    setSent(false);
    setProgress(0);

    const allResults = {};

    // Step 1: System Info
    setCurrentTest("Collecting system information...");
    setProgress(10);
    allResults.system = collectSystemInfo();

    // Step 2: Hardware Info
    setCurrentTest("Reading hardware information...");
    setProgress(20);
    allResults.hardware = collectHardwareInfo();

    // Step 3: Network API Info
    setCurrentTest("Checking network connection...");
    setProgress(30);
    allResults.network = collectNetworkInfo();

    // Step 4: Battery Info
    setCurrentTest("Checking battery status...");
    setProgress(40);
    allResults.battery = await collectBatteryInfo();

    // Step 5: WebGL Info
    setCurrentTest("Testing graphics capabilities...");
    setProgress(50);
    allResults.webgl = collectWebGLInfo();

    // Step 6: Memory Info
    setCurrentTest("Checking memory usage...");
    setProgress(60);
    allResults.memory = collectMemoryInfo();

    // Step 7: CPU Benchmark
    setCurrentTest("Running CPU benchmark...");
    setProgress(55);
    allResults.cpu = await runCPUBenchmark();

    // Step 8: Speed Test (with progress tracking)
    setCurrentTest("Testing internet speed - Measuring latency...");
    setProgress(65);
    allResults.speed = await runSpeedTest((progressData) => {
      if (progressData.phase === "latency") {
        setCurrentTest(`Testing latency... ${Math.round(progressData.progress)}%`);
        setProgress(65 + (progressData.progress * 0.02)); // 65-67%
      } else if (progressData.phase === "download") {
        const mb = progressData.bytes ? (progressData.bytes / (1024 * 1024)).toFixed(1) : "0";
        setCurrentTest(`Downloading test file... ${mb} MB`);
        setProgress(67 + (progressData.progress * 0.02)); // 67-69%
      } else if (progressData.phase === "upload") {
        setCurrentTest(`Uploading test data... ${Math.round(progressData.progress)}%`);
        setProgress(69 + (progressData.progress * 0.01)); // 69-70%
      }
    });

    // Step 9: Storage Info
    setCurrentTest("Checking storage...");
    setProgress(70);
    allResults.storage = await collectStorageInfo();

    // Step 10: Security Info
    setCurrentTest("Checking security settings...");
    setProgress(72);
    allResults.security = collectSecurityInfo();

    // Step 11: CPU Throttling Test (Advanced)
    setCurrentTest("Running CPU throttling detection...");
    setProgress(75);
    allResults.cpuThrottling = await runCPUThrottlingTest(setCurrentTest);

    // Step 12: GPU Stability Test (Advanced)
    setCurrentTest("Running GPU stability test...");
    setProgress(85);
    allResults.gpuStability = await runGPUStabilityTest(setCurrentTest);

    // Step 13: Stress Test (Advanced)
    setCurrentTest("Running combined stress test...");
    setProgress(92);
    allResults.stressTest = await runStressTest(setCurrentTest);

    setProgress(100);
    setCurrentTest("Diagnostic complete!");
    setResults(allResults);
    setIsRunning(false);

    // Automatically run AI analysis
    await runAIAnalysis(allResults);
  };

  // ========== AI ANALYSIS ==========
  const runAIAnalysis = async (diagnosticResults) => {
    setAnalyzingAI(true);
    
    try {
      const response = await axios.post(`${API}/diagnostic/analyze`, {
        results: diagnosticResults
      });
      setAiAnalysis(response.data);
    } catch (error) {
      setAiAnalysis({
        summary: "AI analysis unavailable. Please review the raw data above.",
        recommendations: [],
        issues: []
      });
    }
    
    setAnalyzingAI(false);
  };

  // Send report to SmartIT
  const sendReport = async () => {
    if (!senderInfo.name || !senderInfo.email) {
      toast.error("Please provide your name and email");
      return;
    }
    
    setSending(true);
    setShowSendModal(false);
    try {
      await axios.post(`${API}/diagnostic/report`, {
        results,
        aiAnalysis,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        senderName: senderInfo.name,
        senderEmail: senderInfo.email,
        senderPhone: senderInfo.phone,
        additionalNotes: senderInfo.notes
      });
      setSent(true);
      toast.success("Report sent to SmartIT successfully! We'll contact you soon.");
    } catch (e) {
      toast.error("Failed to send report. Please try again.");
    }
    setSending(false);
  };

  // Open send report modal
  const openSendModal = () => {
    setShowSendModal(true);
  };

  // Create support ticket
  const createTicket = () => {
    // Navigate to login/dashboard to create ticket
    window.location.href = "/login";
  };

  // Helper to render value with support status
  const DataValue = ({ value, isSupported = true }) => {
    if (value === NOT_SUPPORTED || !isSupported) {
      return <span className="text-slate-500 text-sm italic">{NOT_SUPPORTED}</span>;
    }
    return <span className="text-white">{value}</span>;
  };

  // Status indicator
  const StatusBadge = ({ status }) => {
    const colors = {
      supported: "bg-green-500/20 text-green-400 border-green-500/30",
      "not-supported": "bg-slate-500/20 text-slate-400 border-slate-500/30",
      completed: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    };
    return (
      <span className={`px-2 py-0.5 text-xs rounded-full border ${colors[status] || colors.completed}`}>
        {status === "supported" ? "Supported" : status === "not-supported" ? "Not Supported" : "Completed"}
      </span>
    );
  };

  // Severity badge for AI analysis
  const SeverityBadge = ({ level }) => {
    const colors = {
      low: "bg-green-500/20 text-green-400",
      medium: "bg-yellow-500/20 text-yellow-400",
      high: "bg-red-500/20 text-red-400",
    };
    return (
      <span className={`px-2 py-1 text-xs rounded-full font-medium ${colors[level?.toLowerCase()] || colors.low}`}>
        {level || "Low"}
      </span>
    );
  };

  return (
    <MainLayout>
      <SEO {...pageSEO.diagnostic} />
        
      {/* Hero Section */}
      <section className="pt-8 pb-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-cyan-500/5 via-transparent to-transparent" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-cyan-400 text-sm font-medium mb-4">
              <Shield className="w-4 h-4" />
              Real Data Only - No Simulation
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
              SmartIT Quick <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">Diagnostic</span>
            </h1>
            <p className="text-slate-400 max-w-2xl mx-auto text-sm">
              Browser-based diagnostic using only real, accessible data. AI-powered analysis with honest results.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Start Button */}
          {!isRunning && !results && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
              {/* What We Test */}
              <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-6">
                <h2 className="text-lg font-bold text-white mb-4">What This Tool Collects</h2>
                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2 text-slate-300">
                    <CheckCircle className="w-4 h-4 text-green-400" /> Browser & OS information
                  </div>
                  <div className="flex items-center gap-2 text-slate-300">
                    <CheckCircle className="w-4 h-4 text-green-400" /> CPU cores & device memory
                  </div>
                  <div className="flex items-center gap-2 text-slate-300">
                    <CheckCircle className="w-4 h-4 text-green-400" /> Network connection type
                  </div>
                  <div className="flex items-center gap-2 text-slate-300">
                    <CheckCircle className="w-4 h-4 text-green-400" /> Battery status (if available)
                  </div>
                  <div className="flex items-center gap-2 text-slate-300">
                    <CheckCircle className="w-4 h-4 text-green-400" /> Graphics (WebGL) capability
                  </div>
                  <div className="flex items-center gap-2 text-slate-300">
                    <CheckCircle className="w-4 h-4 text-green-400" /> JavaScript performance benchmark
                  </div>
                  <div className="flex items-center gap-2 text-slate-300">
                    <CheckCircle className="w-4 h-4 text-green-400" /> Real internet speed test
                  </div>
                  <div className="flex items-center gap-2 text-slate-300">
                    <CheckCircle className="w-4 h-4 text-green-400" /> Security settings
                  </div>
                </div>
                
                <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                  <p className="text-blue-300 text-sm">
                    <strong>Honest Data Policy:</strong> If a value cannot be read from your browser, it will show "Not supported by this browser" - we never simulate or guess.
                  </p>
                </div>
              </div>

              <div className="text-center">
                <button
                  onClick={runDiagnostics}
                  className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-bold text-lg px-10 py-4 rounded-xl transition-all flex items-center gap-3 mx-auto shadow-lg shadow-cyan-500/20"
                  data-testid="start-diagnostic-btn"
                >
                  <Monitor className="w-6 h-6" />
                  Start Diagnostic
                </button>
              </div>
            </motion.div>
          )}

          {/* Progress */}
          {isRunning && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6">
              <div className="text-center mb-6">
                <Loader2 className="w-10 h-10 text-cyan-400 animate-spin mx-auto mb-3" />
                <h3 className="text-lg font-bold text-white mb-1">Running Diagnostic...</h3>
                <p className="text-cyan-400 text-sm">{currentTest}</p>
              </div>
              
              {/* Progress Bar */}
              <div className="w-full bg-slate-800 rounded-full h-3 overflow-hidden">
                <motion.div 
                  className="h-full bg-gradient-to-r from-cyan-500 to-blue-500"
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
              <p className="text-center text-slate-400 text-sm mt-2">{progress}% complete</p>
            </motion.div>
          )}

          {/* Results */}
          {results && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
              
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 text-center">
                  <p className="text-slate-400 text-xs mb-1">CPU Performance</p>
                  <p className={`text-lg font-bold ${
                    results.cpu.rating === "Excellent" ? "text-green-400" :
                    results.cpu.rating === "Good" ? "text-cyan-400" :
                    results.cpu.rating === "Moderate" ? "text-yellow-400" : "text-red-400"
                  }`}>{results.cpu.rating}</p>
                </div>
                <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 text-center">
                  <p className="text-slate-400 text-xs mb-1">Download</p>
                  <p className={`text-lg font-bold ${
                    results.speed.download === null ? "text-slate-500" :
                    results.speed.download > 50 ? "text-green-400" :
                    results.speed.download > 10 ? "text-yellow-400" : "text-orange-400"
                  }`}>
                    {results.speed.download !== null ? `${results.speed.download} Mbps` : "N/A"}
                  </p>
                </div>
                <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 text-center">
                  <p className="text-slate-400 text-xs mb-1">Upload</p>
                  <p className={`text-lg font-bold ${
                    results.speed.upload === null ? "text-slate-500" :
                    results.speed.upload > 20 ? "text-green-400" :
                    results.speed.upload > 5 ? "text-yellow-400" : "text-orange-400"
                  }`}>
                    {results.speed.upload !== null ? `${results.speed.upload} Mbps` : "N/A"}
                  </p>
                </div>
                <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 text-center">
                  <p className="text-slate-400 text-xs mb-1">Latency</p>
                  <p className={`text-lg font-bold ${
                    results.speed.latency === null ? "text-slate-500" :
                    results.speed.latency < 30 ? "text-green-400" :
                    results.speed.latency < 80 ? "text-yellow-400" : "text-red-400"
                  }`}>
                    {results.speed.latency !== null ? `${results.speed.latency} ms` : "N/A"}
                  </p>
                </div>
                <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 text-center">
                  <p className="text-slate-400 text-xs mb-1">Battery</p>
                  <p className={`text-lg font-bold ${
                    results.battery.supported ? (
                      parseInt(results.battery.level) > 50 ? "text-green-400" :
                      parseInt(results.battery.level) > 20 ? "text-yellow-400" : "text-red-400"
                    ) : "text-slate-500"
                  }`}>
                    {results.battery.level}
                  </p>
                </div>
              </div>

              {/* AI Analysis Section */}
              {analyzingAI ? (
                <div className="bg-slate-900/60 border border-cyan-500/30 rounded-xl p-6">
                  <div className="flex items-center gap-3">
                    <Loader2 className="w-5 h-5 text-cyan-400 animate-spin" />
                    <span className="text-white font-medium">AI is analyzing your results...</span>
                  </div>
                </div>
              ) : aiAnalysis && (
                <div className="bg-slate-900/60 border border-cyan-500/30 rounded-xl overflow-hidden">
                  <div className="px-5 py-3 border-b border-slate-800 bg-cyan-500/5">
                    <h3 className="text-white font-semibold flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-cyan-400" />
                      AI Analysis
                    </h3>
                  </div>
                  <div className="p-5 space-y-4">
                    {/* Summary */}
                    <div>
                      <h4 className="text-slate-400 text-sm mb-2">Summary</h4>
                      <p className="text-white">{aiAnalysis.summary}</p>
                    </div>
                    
                    {/* Issues Found */}
                    {aiAnalysis.issues?.length > 0 && (
                      <div>
                        <h4 className="text-slate-400 text-sm mb-2">Issues Detected</h4>
                        <div className="space-y-2">
                          {aiAnalysis.issues.map((issue, idx) => (
                            <div key={idx} className="flex items-start gap-3 bg-slate-800/50 rounded-lg p-3">
                              <SeverityBadge level={issue.severity} />
                              <div className="flex-1">
                                <p className="text-white text-sm">{issue.description}</p>
                                {issue.suggestion && (
                                  <p className="text-slate-400 text-xs mt-1">{issue.suggestion}</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Recommendations */}
                    {aiAnalysis.recommendations?.length > 0 && (
                      <div>
                        <h4 className="text-slate-400 text-sm mb-2">Recommendations</h4>
                        <ul className="space-y-2">
                          {aiAnalysis.recommendations.map((rec, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm">
                              <CheckCircle className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                              <span className="text-slate-300">{rec}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* SmartIT Services */}
                    {aiAnalysis.smartitServices?.length > 0 && (
                      <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                        <h4 className="text-blue-400 text-sm font-medium mb-2">SmartIT Can Help</h4>
                        <ul className="space-y-1">
                          {aiAnalysis.smartitServices.map((service, idx) => (
                            <li key={idx} className="text-slate-300 text-sm flex items-center gap-2">
                              <ArrowRight className="w-3 h-3 text-blue-400" />
                              {service}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Detailed Results */}
              <div className="grid md:grid-cols-2 gap-4">
                {/* System Info */}
                <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-white font-semibold flex items-center gap-2">
                      <Monitor className="w-4 h-4 text-cyan-400" /> System Info
                    </h3>
                    <StatusBadge status="completed" />
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-slate-400">OS</span><DataValue value={results.system.os} /></div>
                    <div className="flex justify-between"><span className="text-slate-400">Browser</span><DataValue value={`${results.system.browser} ${results.system.browserVersion}`} /></div>
                    <div className="flex justify-between"><span className="text-slate-400">Platform</span><DataValue value={results.system.platform} /></div>
                    <div className="flex justify-between"><span className="text-slate-400">Screen</span><DataValue value={`${results.system.screenWidth} × ${results.system.screenHeight}`} /></div>
                    <div className="flex justify-between"><span className="text-slate-400">Touch Support</span><DataValue value={results.system.touchSupport ? "Yes" : "No"} /></div>
                    <div className="flex justify-between"><span className="text-slate-400">Online</span><DataValue value={results.system.online ? "Yes" : "No"} /></div>
                  </div>
                </div>

                {/* Hardware Info */}
                <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-white font-semibold flex items-center gap-2">
                      <Cpu className="w-4 h-4 text-cyan-400" /> Hardware
                    </h3>
                    <StatusBadge status={results.hardware.cpuCores !== NOT_SUPPORTED ? "supported" : "not-supported"} />
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-slate-400">CPU Cores</span><DataValue value={results.hardware.cpuCores} /></div>
                    <div className="flex justify-between"><span className="text-slate-400">Device Memory</span><DataValue value={results.hardware.deviceMemory} /></div>
                  </div>
                  <div className="mt-3 p-2 bg-slate-800/50 rounded text-xs text-slate-500">
                    Note: Browsers limit hardware info for privacy. Only cores and approximate RAM are available.
                  </div>
                </div>

                {/* CPU Benchmark */}
                <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-white font-semibold flex items-center gap-2">
                      <Zap className="w-4 h-4 text-cyan-400" /> CPU Benchmark
                    </h3>
                    <StatusBadge status="completed" />
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-slate-400">Score</span><span className="text-white">{results.cpu.score} ops/s</span></div>
                    <div className="flex justify-between"><span className="text-slate-400">Rating</span>
                      <span className={
                        results.cpu.rating === "Excellent" ? "text-green-400" :
                        results.cpu.rating === "Good" ? "text-cyan-400" :
                        results.cpu.rating === "Moderate" ? "text-yellow-400" : "text-red-400"
                      }>{results.cpu.rating}</span>
                    </div>
                    <div className="flex justify-between"><span className="text-slate-400">Consistency</span><span className="text-white">{results.cpu.consistency}</span></div>
                  </div>
                </div>

                {/* Network Info */}
                <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-white font-semibold flex items-center gap-2">
                      <Wifi className="w-4 h-4 text-cyan-400" /> Network
                    </h3>
                    <StatusBadge status={results.network.supported ? "supported" : "not-supported"} />
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-slate-400">Connection Type</span><DataValue value={results.network.effectiveType} /></div>
                    <div className="flex justify-between"><span className="text-slate-400">Est. Bandwidth</span><DataValue value={results.network.downlink} /></div>
                    <div className="flex justify-between"><span className="text-slate-400">Est. RTT</span><DataValue value={results.network.rtt} /></div>
                    <div className="flex justify-between"><span className="text-slate-400">Data Saver</span><DataValue value={results.network.saveData} /></div>
                  </div>
                </div>

                {/* Speed Test */}
                <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-white font-semibold flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-cyan-400" /> Speed Test
                    </h3>
                    <StatusBadge status={results.speed.download || results.speed.upload ? "completed" : "failed"} />
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Download</span>
                      <span className={
                        results.speed.download === null ? "text-red-400" :
                        results.speed.download > 50 ? "text-green-400" : 
                        results.speed.download > 10 ? "text-yellow-400" : "text-orange-400"
                      }>
                        {results.speed.download !== null ? `${results.speed.download} Mbps` : "Failed"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Upload</span>
                      <span className={
                        results.speed.upload === null ? "text-red-400" :
                        results.speed.upload > 20 ? "text-green-400" : 
                        results.speed.upload > 5 ? "text-yellow-400" : "text-orange-400"
                      }>
                        {results.speed.upload !== null ? `${results.speed.upload} Mbps` : "Failed"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Latency</span>
                      <span className={
                        results.speed.latency === null ? "text-slate-500" :
                        results.speed.latency < 30 ? "text-green-400" : 
                        results.speed.latency < 80 ? "text-yellow-400" : "text-red-400"
                      }>
                        {results.speed.latency !== null ? `${results.speed.latency} ms` : "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Status</span>
                      <span className={results.speed.testStatus === "Completed" ? "text-green-400" : "text-yellow-400"}>
                        {results.speed.testStatus}
                      </span>
                    </div>
                    {results.speed.error && (
                      <div className="mt-2 p-2 bg-red-900/20 border border-red-800/50 rounded text-red-400 text-xs">
                        {results.speed.error}
                      </div>
                    )}
                  </div>
                </div>

                {/* WebGL Info */}
                <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-white font-semibold flex items-center gap-2">
                      <Monitor className="w-4 h-4 text-cyan-400" /> Graphics (WebGL)
                    </h3>
                    <StatusBadge status={results.webgl.supported ? "supported" : "not-supported"} />
                  </div>
                  {results.webgl.supported ? (
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-slate-400">Version</span><DataValue value={results.webgl.version} /></div>
                      <div className="flex justify-between"><span className="text-slate-400">Vendor</span><DataValue value={results.webgl.vendor} /></div>
                      <div className="py-1">
                        <span className="text-slate-400 block mb-1">Renderer</span>
                        <span className="text-white text-xs break-all">{results.webgl.renderer}</span>
                      </div>
                      <div className="flex justify-between"><span className="text-slate-400">Max Texture</span><DataValue value={`${results.webgl.maxTextureSize} px`} /></div>
                    </div>
                  ) : (
                    <p className="text-slate-500 text-sm">{NOT_SUPPORTED}</p>
                  )}
                </div>

                {/* Memory Info */}
                <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-white font-semibold flex items-center gap-2">
                      <HardDrive className="w-4 h-4 text-cyan-400" /> Memory
                    </h3>
                    <StatusBadge status={results.memory.supported ? "supported" : "not-supported"} />
                  </div>
                  {results.memory.supported ? (
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-slate-400">JS Heap Limit</span><DataValue value={results.memory.jsHeapSizeLimit} /></div>
                      <div className="flex justify-between"><span className="text-slate-400">Total Heap</span><DataValue value={results.memory.totalJSHeapSize} /></div>
                      <div className="flex justify-between"><span className="text-slate-400">Used Heap</span><DataValue value={results.memory.usedJSHeapSize} /></div>
                    </div>
                  ) : (
                    <p className="text-slate-500 text-sm">{NOT_SUPPORTED} (Chrome only)</p>
                  )}
                </div>

                {/* Battery Info */}
                <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-white font-semibold flex items-center gap-2">
                      <Settings className="w-4 h-4 text-cyan-400" /> Battery
                    </h3>
                    <StatusBadge status={results.battery.supported ? "supported" : "not-supported"} />
                  </div>
                  {results.battery.supported ? (
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-slate-400">Level</span><DataValue value={results.battery.level} /></div>
                      <div className="flex justify-between"><span className="text-slate-400">Charging</span><DataValue value={results.battery.charging} /></div>
                      <div className="flex justify-between"><span className="text-slate-400">Time to Full</span><DataValue value={results.battery.chargingTime} /></div>
                      <div className="flex justify-between"><span className="text-slate-400">Time Remaining</span><DataValue value={results.battery.dischargingTime} /></div>
                    </div>
                  ) : (
                    <p className="text-slate-500 text-sm">{NOT_SUPPORTED}</p>
                  )}
                </div>

                {/* Security Info */}
                <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-white font-semibold flex items-center gap-2">
                      <Shield className="w-4 h-4 text-cyan-400" /> Security
                    </h3>
                    <StatusBadge status="completed" />
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-slate-400">HTTPS</span>
                      <span className={results.security.https ? "text-green-400" : "text-red-400"}>
                        {results.security.https ? "Yes" : "No"}
                      </span>
                    </div>
                    <div className="flex justify-between"><span className="text-slate-400">Secure Context</span>
                      <span className={results.security.secureContext ? "text-green-400" : "text-yellow-400"}>
                        {results.security.secureContext ? "Yes" : "No"}
                      </span>
                    </div>
                    <div className="flex justify-between"><span className="text-slate-400">Cookies</span><DataValue value={results.security.cookiesEnabled ? "Enabled" : "Disabled"} /></div>
                    <div className="flex justify-between"><span className="text-slate-400">Do Not Track</span><DataValue value={results.security.doNotTrack ? "Enabled" : "Disabled"} /></div>
                  </div>
                </div>

                {/* Storage Info */}
                <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-white font-semibold flex items-center gap-2">
                      <HardDrive className="w-4 h-4 text-cyan-400" /> Browser Storage
                    </h3>
                    <StatusBadge status={results.storage.supported ? "supported" : "not-supported"} />
                  </div>
                  {results.storage.supported ? (
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Quota (Max Available)</span>
                        <span className="text-white">{results.storage.quotaMB}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Usage (Currently Used)</span>
                        <span className="text-white">{results.storage.usageMB}</span>
                      </div>
                      <div className="mt-3 p-2 bg-blue-500/10 border border-blue-500/20 rounded text-xs text-blue-300">
                        <strong>Note:</strong> This shows browser storage (IndexedDB, Cache, etc.), not your system disk. For security reasons, browsers cannot access real disk storage information.
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-slate-500 text-sm">{results.storage.message}</p>
                      <div className="p-2 bg-slate-800/50 rounded text-xs text-slate-400">
                        For security reasons, browsers cannot access real disk storage. Only browser storage usage can be displayed.
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Advanced Performance Degradation Tests */}
              <div className="mt-6">
                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-orange-400" />
                  Advanced Performance Tests
                </h2>
                <p className="text-slate-400 text-sm mb-4">
                  These tests detect potential thermal throttling and performance degradation through indirect measurements. 
                  <span className="text-slate-500 italic"> (Browsers cannot access CPU/GPU temperatures directly.)</span>
                </p>
                
                <div className="grid md:grid-cols-3 gap-4">
                  {/* CPU Throttling Test */}
                  <div className={`border rounded-xl p-4 ${
                    results.cpuThrottling?.severity === 'bad' ? 'bg-red-500/10 border-red-500/30' :
                    results.cpuThrottling?.severity === 'moderate' ? 'bg-yellow-500/10 border-yellow-500/30' :
                    'bg-slate-900/60 border-slate-800'
                  }`}>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                        <Cpu className="w-4 h-4 text-orange-400" /> CPU Throttling
                      </h3>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        results.cpuThrottling?.severity === 'bad' ? 'bg-red-500/20 text-red-400' :
                        results.cpuThrottling?.severity === 'moderate' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-green-500/20 text-green-400'
                      }`}>
                        {results.cpuThrottling?.throttlingDetected || "None"}
                      </span>
                    </div>
                    {results.cpuThrottling?.supported ? (
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-400">First Score</span>
                          <span className="text-white">{results.cpuThrottling.firstScore}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Last Score</span>
                          <span className="text-white">{results.cpuThrottling.lastScore}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Drop</span>
                          <span className={
                            results.cpuThrottling.dropPercent > 20 ? 'text-red-400' :
                            results.cpuThrottling.dropPercent > 10 ? 'text-yellow-400' : 'text-green-400'
                          }>
                            {results.cpuThrottling.dropPercent}%
                          </span>
                        </div>
                        <p className="text-xs text-slate-400 mt-2 pt-2 border-t border-slate-700/50">
                          {results.cpuThrottling.message}
                        </p>
                      </div>
                    ) : (
                      <p className="text-slate-500 text-sm">{NOT_SUPPORTED}</p>
                    )}
                  </div>

                  {/* GPU Stability Test */}
                  <div className={`border rounded-xl p-4 ${
                    results.gpuStability?.severity === 'bad' ? 'bg-red-500/10 border-red-500/30' :
                    results.gpuStability?.severity === 'moderate' ? 'bg-yellow-500/10 border-yellow-500/30' :
                    'bg-slate-900/60 border-slate-800'
                  }`}>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                        <Monitor className="w-4 h-4 text-orange-400" /> GPU FPS Stability
                      </h3>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        results.gpuStability?.severity === 'bad' ? 'bg-red-500/20 text-red-400' :
                        results.gpuStability?.severity === 'moderate' ? 'bg-yellow-500/20 text-yellow-400' :
                        results.gpuStability?.supported ? 'bg-green-500/20 text-green-400' : 'bg-slate-500/20 text-slate-400'
                      }`}>
                        {results.gpuStability?.degradationDetected || (results.gpuStability?.supported === false ? "N/A" : "None")}
                      </span>
                    </div>
                    {results.gpuStability?.supported ? (
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-400">Avg FPS</span>
                          <span className="text-white">{results.gpuStability.avgFPS}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Min / Max</span>
                          <span className="text-white">{results.gpuStability.minFPS} / {results.gpuStability.maxFPS}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">Drop</span>
                          <span className={
                            results.gpuStability.dropPercent > 20 ? 'text-red-400' :
                            results.gpuStability.dropPercent > 10 ? 'text-yellow-400' : 'text-green-400'
                          }>
                            {results.gpuStability.dropPercent}%
                          </span>
                        </div>
                        <p className="text-xs text-slate-400 mt-2 pt-2 border-t border-slate-700/50">
                          {results.gpuStability.message}
                        </p>
                      </div>
                    ) : (
                      <p className="text-slate-500 text-sm">{results.gpuStability?.message || NOT_SUPPORTED}</p>
                    )}
                  </div>

                  {/* Stress Test */}
                  <div className={`border rounded-xl p-4 ${
                    results.stressTest?.severity === 'bad' ? 'bg-red-500/10 border-red-500/30' :
                    results.stressTest?.severity === 'moderate' ? 'bg-yellow-500/10 border-yellow-500/30' :
                    'bg-slate-900/60 border-slate-800'
                  }`}>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-white font-semibold text-sm flex items-center gap-2">
                        <Zap className="w-4 h-4 text-orange-400" /> Stress Test
                      </h3>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        results.stressTest?.severity === 'bad' ? 'bg-red-500/20 text-red-400' :
                        results.stressTest?.severity === 'moderate' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-green-500/20 text-green-400'
                      }`}>
                        {results.stressTest?.overallResult || "Unknown"}
                      </span>
                    </div>
                    {results.stressTest?.supported ? (
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-400">Duration</span>
                          <span className="text-white">{results.stressTest.duration}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">CPU Drop</span>
                          <span className={
                            results.stressTest.cpuDropPercent > 20 ? 'text-red-400' :
                            results.stressTest.cpuDropPercent > 10 ? 'text-yellow-400' : 'text-green-400'
                          }>
                            {results.stressTest.cpuDropPercent}%
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-400">GPU Drop</span>
                          <span className={
                            typeof results.stressTest.gpuDropPercent === 'number' ? (
                              results.stressTest.gpuDropPercent > 20 ? 'text-red-400' :
                              results.stressTest.gpuDropPercent > 10 ? 'text-yellow-400' : 'text-green-400'
                            ) : 'text-slate-500'
                          }>
                            {results.stressTest.gpuDropPercent}%
                          </span>
                        </div>
                        <p className="text-xs text-slate-400 mt-2 pt-2 border-t border-slate-700/50">
                          {results.stressTest.message}
                        </p>
                      </div>
                    ) : (
                      <p className="text-slate-500 text-sm">{NOT_SUPPORTED}</p>
                    )}
                  </div>
                </div>

                {/* Disclaimer */}
                <div className="mt-4 p-3 bg-slate-800/50 border border-slate-700/50 rounded-lg">
                  <p className="text-slate-400 text-xs">
                    <strong className="text-slate-300">Note:</strong> These tests use indirect performance measurements to detect potential thermal throttling. 
                    Browsers cannot access CPU/GPU temperatures, voltages, fan speeds, or hardware sensors for security reasons. 
                    Results may be affected by background processes and system load.
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 justify-center pt-4">
                <button
                  onClick={runDiagnostics}
                  className="bg-slate-800 hover:bg-slate-700 text-white font-medium px-6 py-3 rounded-xl transition-all flex items-center gap-2"
                >
                  <ArrowRight className="w-4 h-4" /> Run Again
                </button>
                <button
                  onClick={openSendModal}
                  disabled={sending || sent}
                  className={`font-medium px-6 py-3 rounded-xl transition-all flex items-center gap-2 ${
                    sent ? "bg-green-500/20 text-green-400 border border-green-500/30" :
                    "bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white"
                  }`}
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : sent ? <CheckCircle className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                  {sent ? "Report Sent" : sending ? "Sending..." : "Send Report to SmartIT"}
                </button>
                <button
                  onClick={createTicket}
                  className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/30 font-medium px-6 py-3 rounded-xl transition-all flex items-center gap-2"
                >
                  <Ticket className="w-4 h-4" /> Create Support Ticket
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Send Report Modal */}
      <AnimatePresence>
        {showSendModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowSendModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-md"
            >
              <h3 className="text-xl font-bold text-white mb-2">Send Report to SmartIT</h3>
              <p className="text-slate-400 text-sm mb-6">Please provide your contact details so we can follow up with you.</p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Your Name *</label>
                  <input
                    type="text"
                    value={senderInfo.name}
                    onChange={(e) => setSenderInfo({...senderInfo, name: e.target.value})}
                    placeholder="John Smith"
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
                    data-testid="sender-name-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Your Email *</label>
                  <input
                    type="email"
                    value={senderInfo.email}
                    onChange={(e) => setSenderInfo({...senderInfo, email: e.target.value})}
                    placeholder="john@example.com"
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
                    data-testid="sender-email-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Phone Number (optional)</label>
                  <input
                    type="tel"
                    value={senderInfo.phone}
                    onChange={(e) => setSenderInfo({...senderInfo, phone: e.target.value})}
                    placeholder="+44 7XXX XXXXXX"
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
                    data-testid="sender-phone-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Describe your issue (optional)</label>
                  <textarea
                    value={senderInfo.notes}
                    onChange={(e) => setSenderInfo({...senderInfo, notes: e.target.value})}
                    placeholder="My computer is running slow, or I'm experiencing crashes..."
                    rows={3}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none resize-none"
                    data-testid="sender-notes-input"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowSendModal(false)}
                  className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-medium py-3 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={sendReport}
                  disabled={!senderInfo.name || !senderInfo.email}
                  className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-medium py-3 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="send-report-btn"
                >
                  <Send className="w-4 h-4" />
                  Send Report
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </MainLayout>
  );
};

export default DiagnosticPage;
